import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { Translate, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAppDispatch, useAppSelector } from 'app/config/store';

import { IVehicle } from 'app/shared/model/vehicle.model';
import { getEntity, updateEntity, createEntity, reset } from './vehicle.reducer';

export const VehicleUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const vehicleEntity = useAppSelector(state => state.vehicle.entity);
  const loading = useAppSelector(state => state.vehicle.loading);
  const updating = useAppSelector(state => state.vehicle.updating);
  const updateSuccess = useAppSelector(state => state.vehicle.updateSuccess);

  const [formData, setFormData] = useState<any>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleClose = () => {
    navigate('/vehicle' + location.search);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
      setFormData({});
    } else {
      dispatch(getEntity(id));
    }
  }, []);

  useEffect(() => {
    if (vehicleEntity) {
      setFormData({
        ...vehicleEntity,
      });
    }
  }, [vehicleEntity]);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.plate || formData.plate.trim() === '') {
      newErrors.plate = 'Plate is required';
    }
    if (!formData.type || formData.type.trim() === '') {
      newErrors.type = 'Type is required';
    }
    if (!formData.capacity) {
      newErrors.capacity = 'Capacity is required';
    } else if (isNaN(Number(formData.capacity))) {
      newErrors.capacity = 'Must be a number';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // eslint-disable-next-line complexity
  const saveEntity = () => {
    if (!validate()) {
      return;
    }

    const values = { ...formData };
    if (values.id !== undefined && typeof values.id !== 'number') {
      values.id = Number(values.id);
    }
    if (values.capacity !== undefined && typeof values.capacity !== 'number') {
      values.capacity = Number(values.capacity);
    }

    values.enabled = values.enabled === true;

    const entity = {
      ...vehicleEntity,
      ...values,
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, p: 2 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h2">
          <Translate contentKey="jhipsterSampleApplicationApp.vehicle.home.createOrEditLabel">Create or edit a Vehicle</Translate>
        </Typography>
      </Box>
      <Box component="form" sx={{ '& .MuiTextField-root': { mb: 2 } }} noValidate autoComplete="off">
        {loading ? (
          <Typography>Loading...</Typography>
        ) : (
          <Box>
            {!isNew && (
              <TextField
                label={translate('global.field.id')}
                id="vehicle-id"
                name="id"
                value={formData.id || ''}
                fullWidth
                InputProps={{ readOnly: true }}
                sx={{ mb: 2 }}
              />
            )}
            <TextField
              label={translate('jhipsterSampleApplicationApp.vehicle.plate')}
              id="vehicle-plate"
              name="plate"
              data-cy="plate"
              value={formData.plate || ''}
              onChange={handleChange('plate')}
              fullWidth
              required
              error={!!errors.plate}
              helperText={errors.plate || ''}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.enabled || false}
                  onChange={handleChange('enabled')}
                  id="vehicle-enabled"
                  name="enabled"
                  data-cy="enabled"
                />
              }
              label={translate('jhipsterSampleApplicationApp.vehicle.enabled')}
            />
            <TextField
              label={translate('jhipsterSampleApplicationApp.vehicle.type')}
              id="vehicle-type"
              name="type"
              data-cy="type"
              value={formData.type || ''}
              onChange={handleChange('type')}
              fullWidth
              required
              error={!!errors.type}
              helperText={errors.type || ''}
            />
            <TextField
              label={translate('jhipsterSampleApplicationApp.vehicle.capacity')}
              id="vehicle-capacity"
              name="capacity"
              data-cy="capacity"
              value={formData.capacity || ''}
              onChange={handleChange('capacity')}
              fullWidth
              required
              type="number"
              error={!!errors.capacity}
              helperText={errors.capacity || ''}
            />
            <TextField
              label={translate('jhipsterSampleApplicationApp.vehicle.color')}
              id="vehicle-color"
              name="color"
              data-cy="color"
              value={formData.color || ''}
              onChange={handleChange('color')}
              fullWidth
            />
            <TextField
              label={translate('jhipsterSampleApplicationApp.vehicle.defaultImage')}
              id="vehicle-defaultImage"
              name="defaultImage"
              data-cy="defaultImage"
              value={formData.defaultImage || ''}
              onChange={handleChange('defaultImage')}
              fullWidth
            />
            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button
                component={Link}
                id="cancel-save"
                data-cy="entityCreateCancelButton"
                to="/vehicle"
                replace
                color="info"
                variant="outlined"
                startIcon={<FontAwesomeIcon icon="arrow-left" />}
              >
                <Translate contentKey="entity.action.back">Back</Translate>
              </Button>
              <Button
                color="primary"
                id="save-entity"
                data-cy="entityCreateSaveButton"
                onClick={saveEntity}
                disabled={updating}
                variant="contained"
                startIcon={<FontAwesomeIcon icon="save" />}
              >
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default VehicleUpdate;
